import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ModalController, PickerController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Answer, QuantitativeElement, Question } from '@haleo-frontend/data-access/models';

import { AddElementModal } from './add-element.modal';

@Component({
    selector: 'app-thought-record-question-quantitative-list',
    templateUrl: 'quantitative-list.component.html',
    styleUrls: ['quantitative-list.component.scss']
})
export class QuantitativeListComponent implements OnChanges {

    @Input() question: Question;
    @Input() answer: Answer | undefined;
    @Input() integrated: boolean;
    @Input() readOnly: boolean;
    @Output() answered: EventEmitter<any>;

    options: {
        options: { key: string, label: string }[],
        range: { start: number, end: number },
        unit: string
    };
    elements: QuantitativeElement[];

    constructor(private modalController: ModalController,
                private pickerController: PickerController,
                private translateService: TranslateService) {
        this.answered = new EventEmitter<any>();
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (this.question) {
            this.options = JSON.parse(this.question.options);
        }

        if (this.answer && this.answer.value) {
            this.elements = JSON.parse(this.answer.value);
        } else {
            this.elements = [];
        }
    }

    async addElement() {

        const options = this.options.options.filter(option => {
            return this.elements.findIndex(element => element.key === option.key) < 0;
        });

        const data = {
            options: options,
            range: this.options.range,
            unit: this.options.unit,
        };

        const modal = await this.modalController.create({
            component: AddElementModal,
            componentProps: data,
            mode: 'ios',
            backdropDismiss: true
        });
        await modal.present();

        const result = await modal.onDidDismiss();

        if (result.data) {

            const element = result.data;
            const value = await this.askElementValue(element);
            this.elements.push({key: element.key, label: element.label, value, oldValue: null});
        }
    }

    async update(element: any) {

        const rangeOptions: any = [];
        const increment = 5;

        for (let i = this.options.range.start; i <= this.options.range.end; i += increment) {
            rangeOptions.push({text: i, value: i});
        }

        const picker = await this.pickerController.create({
            mode: 'ios',
            cssClass: 'quantitative-list two-columns',
            buttons: [
                {text: element.label, handler: () => false,},
                {text: this.translateService.instant('thoughtRecord.assessment.rateLevel'), handler: () => false,},
                {text: this.translateService.instant('thoughtRecord.assessment.done')},
                {text: element.oldValue, handler: () => false}
            ],
            columns: [{name: 'number', options: rangeOptions, suffix: this.options.unit}]
        });

        await picker.present();
        await picker.onDidDismiss();

        const pickerValues = await picker.getColumn('number');

        if (pickerValues?.selectedIndex) {
            const value = pickerValues.options[pickerValues.selectedIndex].value;

            const elementIndex = this.elements.findIndex(e => e.key === element.key);
            this.elements[elementIndex].value = value;
        }
    }

    remove(elementIndex: number) {
        this.elements.splice(elementIndex, 1);
    }

    continue() {
        this.answered.emit(JSON.stringify(this.elements));
    }

    private async askElementValue(element: { key: string, label: string }) {

        const rangeOptions: any = [];
        const increment = 5;

        for (let i = this.options.range.start; i <= this.options.range.end; i += increment) {
            rangeOptions.push({text: i, value: i});
        }

        const picker = await this.pickerController.create({
            mode: 'ios',
            cssClass: 'quantitative-list',
            buttons: [
                {text: element.label, handler: () => false,},
                {text: this.translateService.instant('thoughtRecord.assessment.rateLevel'), handler: () => false,},
                {text: this.translateService.instant('thoughtRecord.assessment.add')}
            ],
            columns: [{name: 'number', options: rangeOptions, suffix: this.options.unit}]
        });

        await picker.present();
        await picker.onDidDismiss();

        const pickerValues = await picker.getColumn('number');

        if (pickerValues?.selectedIndex) {
            return pickerValues.options[pickerValues.selectedIndex].value;
        } else {
            return null;
        }
    }
}
